import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin, X, Search } from 'lucide-react';
import { getGoogleMapsApiKey } from '@/lib/api-keys';
import { useDebounce } from '@/hooks/use-debounce';
import { useToast } from '@/hooks/use-toast';
import { LocationConfirmationSection } from './location-confirmation-section';
import { loadGoogleMapsApi, isGoogleMapsLoaded } from '@/lib/google-maps-loader';

// Interface para os resultados da Places API v1
interface PlaceResult {
  id: string;
  displayName?: {
    text: string;
    languageCode: string;
  };
  formattedAddress?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  rating?: number;
  userRatingCount?: number;
  addressComponents?: {
    types: string[];
    longText: string;
  }[];
}

// Adicionar interface para o resultado da API JavaScript do Google
interface PlaceResultJS extends google.maps.places.PlaceResult {
  address_components?: {
    types: string[];
    long_name: string;
    short_name: string;
  }[];
}

// Interface para a resposta da API
interface PlacesApiResponse {
  places: PlaceResult[];
}

// Interface para as props do componente
interface PlacesAutocompleteV1Props {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (placeData: {
    placeId: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
    googleMapsUrl: string;
    city: string;
    country: string;
  }) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showNameInsteadOfAddress?: boolean;
}

export function PlacesAutocompleteV1({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Digite um local",
  className = "",
  disabled = false,
  showNameInsteadOfAddress = true
}: PlacesAutocompleteV1Props) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<PlaceResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedValue = useDebounce(value, 600);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<{
    placeId: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
    googleMapsUrl: string;
    city: string;
    country: string;
  } | null>(null);
  const [placeConfirmed, setPlaceConfirmed] = useState(false);
  
  // Carregar o script do Google Maps se ainda não estiver carregado
  useEffect(() => {
    if (!isGoogleMapsLoaded()) {
      loadGoogleMapsApi().catch(error => {
        console.error('Erro ao carregar a API do Google Maps:', error);
      });
    }
  }, [toast]);
  
  // Verificar se já existe um valor inicial (local já confirmado)
  useEffect(() => {
    // Se o componente já iniciar com um valor, consideramos que um local já foi confirmado
    if (value && value.trim() !== '') {
      setPlaceConfirmed(true);
    }
  }, []);
  
  // Fechar sugestões ao clicar fora do componente
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Função para iniciar a busca manualmente
  const handleSearch = () => {
    if (value.trim().length < 3) {
      toast({
        title: "Texto muito curto",
        description: "Digite pelo menos 3 caracteres para buscar locais.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setSearchQuery(value);
    fetchSuggestions(value);
  };

  // Handler para o evento de tecla pressionada
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };
  
  // Função para buscar sugestões usando a nova Places API v1
  const fetchSuggestions = async (query: string) => {
    // Verificar se a API está carregada antes de prosseguir
    if (!isGoogleMapsLoaded()) {
      try {
        await loadGoogleMapsApi();
      } catch (error) {
        console.error('Erro ao carregar a API do Google Maps:', error);
        toast({
          title: "Erro na configuração",
          description: "Não foi possível carregar a API do Google Maps.",
          variant: "destructive"
        });
        return;
      }
    }

    const apiKey = getGoogleMapsApiKey();
    
    if (!apiKey) {
      console.error('Google Maps API Key não configurada');
      toast({
        title: "Erro na configuração",
        description: "Chave da API do Google Maps não configurada.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Endpoint da nova Places API (v1)
      const url = 'https://places.googleapis.com/v1/places:searchText';
      
      // Corpo da requisição
      const requestBody = {
        textQuery: query,
        languageCode: "pt-BR",
        maxResultCount: 10
      };
      
      // Fazer a requisição
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.id'
        },
        body: JSON.stringify(requestBody)
      });
      
      // Verificar se a resposta foi bem-sucedida
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Erro na requisição: ${response.status} - ${errorText}`);
        
        // Tentar fallback para a API JavaScript se disponível
        tryFallbackSearch(query);
        return;
      }
      
      // Processar os resultados
      const data: PlacesApiResponse = await response.json();
      
      if (data.places && data.places.length > 0) {
        setSuggestions(data.places);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        toast({
          title: "Nenhum resultado",
          description: "Não foram encontrados locais com esse nome.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Erro ao buscar sugestões:', error);
      
      // Tentar fallback para a API JavaScript se disponível
      tryFallbackSearch(query);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Função de fallback para usar a API JavaScript do Google Maps se disponível
  const tryFallbackSearch = (query: string) => {
    // Verificar se a API JavaScript do Google Maps está disponível
    if (window.google && window.google.maps && window.google.maps.places) {
      
      // Criar um elemento temporário para o serviço Places
      const placesService = new window.google.maps.places.AutocompleteService();
      
      placesService.getPlacePredictions(
        { input: query },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
            // Converter os resultados para o formato esperado
            const convertedResults: PlaceResult[] = predictions.map(prediction => ({
              id: prediction.place_id,
              displayName: {
                text: prediction.structured_formatting?.main_text || prediction.description,
                languageCode: 'pt-BR'
              },
              formattedAddress: prediction.structured_formatting?.secondary_text || prediction.description
            }));
            
            setSuggestions(convertedResults);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
            toast({
              title: "Nenhum resultado",
              description: "Não foram encontrados locais com esse nome.",
              variant: "default"
            });
          }
        }
      );
    } else {
      // Se não tiver a API JavaScript disponível, mostrar mensagem de erro
      toast({
        title: "Erro na busca",
        description: "Não foi possível buscar sugestões. Tente novamente mais tarde.",
        variant: "destructive"
      });
      
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  // Função para obter detalhes de um lugar selecionado
  const getPlaceDetails = async (placeId: string) => {
    const apiKey = getGoogleMapsApiKey();
    
    if (!apiKey) {
      console.error('Google Maps API Key não configurada');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Endpoint da nova Places API (v1)
      const url = `https://places.googleapis.com/v1/places/${placeId}`;
      
      // Fazer a requisição
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'displayName,formattedAddress,location,id,photos,addressComponents'
        }
      });
      
      // Verificar se a resposta foi bem-sucedida
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Erro ao obter detalhes do lugar: ${response.status} - ${errorText}`);
        
        // Tentar fallback para a API JavaScript se disponível
        tryFallbackGetDetails(placeId);
        return;
      }
      
      // Processar os resultados
      const place: PlaceResult = await response.json();
      
      if (place && place.id) {
        // Make sure the coordinates are properly structured for maps to show markers
        // The format must be {lat: number, lng: number} for compatibility with Google Maps
        // and our own map components (QuotationMap, etc.)
        let coordinates = { lat: 0, lng: 0 };
        
        if (place.location?.latitude !== undefined && place.location?.longitude !== undefined) {
          coordinates = {
            lat: place.location.latitude,
            lng: place.location.longitude
          };
        }
        
        // Criar objeto com os dados do lugar
        const placeData = {
          placeId: place.id,
          name: place.displayName?.text || '',
          address: place.formattedAddress || '',
          coordinates: coordinates,
          // Try to use the URL from the API first if available
          googleMapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.displayName?.text || 'Local')}&query_place_id=${place.id}`,
          // Extrair cidade e país dos componentes do endereço
          city: place.addressComponents?.find((component) => 
            component.types && (component.types.includes('locality') || component.types.includes('administrative_area_level_2'))
          )?.longText || '',
          country: place.addressComponents?.find((component) => 
            component.types && component.types.includes('country')
          )?.longText || ''
        };
        
        // If address is empty but we have a name, use name as fallback for address
        if (!placeData.address && placeData.name) {
          placeData.address = placeData.name;
        }
        
        // Atualizar o valor do input com o nome ou endereço, dependendo da configuração
        onChange(showNameInsteadOfAddress ? placeData.name : placeData.address);
        
        // Armazenar o lugar selecionado para confirmação
        setSelectedPlace(placeData);
        
        // Mostrar a seção de confirmação
        setShowConfirmation(true);
        
        // Fechar as sugestões
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Erro ao obter detalhes do lugar:', error);
      
      // Tentar fallback para a API JavaScript se disponível
      tryFallbackGetDetails(placeId);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Função de fallback para usar a API JavaScript do Google Maps para obter detalhes do lugar
  const tryFallbackGetDetails = (placeId: string) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      
      // Criar elemento HTML temporário para o serviço Places
      const tempMapDiv = document.createElement('div');
      const placesService = new window.google.maps.places.PlacesService(tempMapDiv);
      
      placesService.getDetails(
        { placeId: placeId, fields: ['name', 'formatted_address', 'geometry', 'url'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            
            // Convert the data to our format
            let coordinates = { lat: 0, lng: 0 };
            
            if (place.geometry?.location) {
              coordinates = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              };
            }
            
            const placeData = {
              placeId: placeId,
              name: place.name || '',
              address: place.formatted_address || '',
              coordinates: coordinates,
              googleMapsUrl: place.url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name || 'Local')}&query_place_id=${placeId}`,
              // Extrair cidade e país dos componentes do endereço
              city: place.address_components?.find((component) => 
                component.types && (component.types.includes('locality') || component.types.includes('administrative_area_level_2'))
              )?.long_name || '',
              country: place.address_components?.find((component) => 
                component.types && component.types.includes('country')
              )?.long_name || ''
            };

            // If address is empty but we have a name, use name as fallback for address
            if (!placeData.address && placeData.name) {
              placeData.address = placeData.name;
            }
            
            // Update UI
            onChange(showNameInsteadOfAddress ? placeData.name : placeData.address);
            setSelectedPlace(placeData);
            setShowConfirmation(true);
          } else {
            console.error('Erro ao obter detalhes do lugar (API JavaScript):', status);
            toast({
              title: "Erro ao obter detalhes",
              description: "Não foi possível obter os detalhes deste local.",
              variant: "destructive"
            });
          }
        }
      );
    } else {
      toast({
        title: "Erro na busca",
        description: "Não foi possível obter detalhes deste local.",
        variant: "destructive"
      });
    }
  };
  
  // Função para selecionar um lugar
  const handleSelectPlace = (place: PlaceResult) => {
    if (place.id) {
      getPlaceDetails(place.id);
    }
  };
  
  // Função para limpar o input e desvincular o local
  const handleClearInput = () => {
    onChange('');
    setSuggestions([]);
    setShowSuggestions(false);
    setShowConfirmation(false);
    setSelectedPlace(null);
    setPlaceConfirmed(false);
    
    // Se um local foi confirmado anteriormente, notificar que foi desvinculado
    if (placeConfirmed) {
      // Notificar o componente pai que o local foi desvinculado
      onPlaceSelect?.({
        placeId: '',
        name: '',
        address: '',
        coordinates: { lat: 0, lng: 0 },
        googleMapsUrl: '',
        city: '',
        country: ''
      });
      
      toast({
        title: "Local desvinculado",
        description: "O local foi removido com sucesso.",
        variant: "default",
      });
    }
  };
  
  // Função para confirmar o local selecionado
  const handleConfirmPlace = () => {
    if (selectedPlace) {
      // Ensure address is not empty
      if (!selectedPlace.address) {
        // If address is empty, set it to the name as a fallback
        selectedPlace.address = selectedPlace.name || '';
      }

      // IMPORTANTE: Garantir que as coordenadas sejam números válidos
      // Isso evita problemas com referências que podem ser perdidas
      const normalizedCoordinates = {
        lat: Number(selectedPlace.coordinates.lat),
        lng: Number(selectedPlace.coordinates.lng)
      };

      // Use the existing URL if available, only create a new one if not present
      // This preserves the original URL from the Google Maps API
      const googleMapsUrl = selectedPlace.googleMapsUrl || 
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedPlace.name || 'Local')}&query_place_id=${selectedPlace.placeId}`;

      // Criar objeto com coordenadas normalizadas
      const normalizedPlace = {
        ...selectedPlace,
        address: selectedPlace.address, // Ensure address is passed correctly
        coordinates: normalizedCoordinates,
        googleMapsUrl: googleMapsUrl,
        city: selectedPlace.city,
        country: selectedPlace.country
      };
      
      // Notificar sobre o local selecionado com coordenadas normalizadas
      onPlaceSelect(normalizedPlace);
      
      // Marcar como confirmado e fechar a confirmação
      setPlaceConfirmed(true);
      setShowConfirmation(false);
      
      // Fechar as sugestões também
      setShowSuggestions(false);
    }
  };
  
  // Função para cancelar a seleção
  const handleCancelSelection = () => {
    setShowConfirmation(false);
    setSelectedPlace(null);
    onChange('');
    setPlaceConfirmed(false);
  };
  
  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setInputFocused(true);
          }}
          onBlur={() => {
            // Pequeno atraso para permitir que cliques em sugestões funcionem antes de esconder
            setTimeout(() => {
              setInputFocused(false);
            }, 200);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`placeholder:text-[10px] placeholder:text-gray-400 ${className}`}
          disabled={disabled || isLoading}
          ref={inputRef}
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        )}
        
        {!isLoading && (value || placeConfirmed) && (
          <button
            type="button"
            onClick={handleClearInput}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Limpar campo e desvincular local"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        
        {!isLoading && !value && !placeConfirmed && (
          <button
            type="button"
            onClick={handleSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-voah-teal hover:text-voah-teal-dark"
            aria-label="Buscar local"
          >
            <Search className="h-4 w-4" />
          </button>
        )}
        
        {!isLoading && value && !placeConfirmed && (
          <button
            type="button"
            onClick={handleSearch}
            className="absolute right-8 top-1/2 transform -translate-y-1/2 text-voah-teal hover:text-voah-teal-dark"
            aria-label="Buscar local"
          >
            <Search className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((place) => {
            // Usar o endereço completo sem processamento
            const fullAddress = place.formattedAddress || '';

            return (
              <div
                key={place.id}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-start gap-2"
                onClick={() => handleSelectPlace(place)}
              >
                <MapPin className="h-4 w-4 text-voah-teal mt-1 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium text-sm">
                    {place.displayName?.text || 'Local sem nome'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {fullAddress}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {selectedPlace && isGoogleMapsLoaded() && (
        <LocationConfirmationSection
          isOpen={showConfirmation}
          location={selectedPlace}
          onConfirm={handleConfirmPlace}
          onCancel={handleCancelSelection}
        />
      )}
      
      {placeConfirmed && (
        <div className="text-xs text-green-600 mt-1 flex items-center">
          <span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-1"></span>
          Local confirmado (marcador no mapa)
        </div>
      )}
      {!placeConfirmed && value && (
        <div className="text-xs text-amber-600 mt-1 flex items-center">
          <span className="inline-block w-2 h-2 bg-amber-600 rounded-full mr-1"></span>
          Selecione um local da lista para mostrar no mapa
        </div>
      )}
    </div>
  );
} 